/**
 * **The `request_attack` an armed aim resolves as against one clicked unit**
 * (`projects/target-hover-forecast.md` 3.1). `CombatApp.fireAt` sends {@link directAttackIntent}'s
 * intent for the modes that fire one plain `request_attack`, and the targeting-mode hover forecast
 * (`TargetForecastPanel`) reads {@link attackIntentForAim} for the same aim and target, so the
 * forecast and the click are built from one intent and cannot disagree about weapon, profile,
 * accuracy or preselected choices.
 *
 * It lives beside `predicates.ts` rather than in `lib/encounter/attackIntent.ts` because the
 * Skirmish branch needs `resolveSkirmishFireChoices`, which is route code.
 */
import { heldAction, type EncounterState } from "@lancer-vtt/domain";
import { quickTechIntent, quickTechOption, techMenuPick } from "@lancer-vtt/rules";
import type { RequestActivate, RequestAttack, RequestFullTech } from "@lancer-vtt/shared";
import { buildAttackIntent, buildFullTechIntent, buildReleaseIntent } from "../../../lib/encounter/attackIntent";
import { weaponModeThisTurn } from "../../../lib/encounter/attackRange";
import type { ActionDraft } from "../../../lib/encounter/combatInspector";
import { resolveSkirmishFireChoices } from "./predicates";

/** The aims `fireAt` fires as exactly one `request_attack` built here. */
export type DirectAttackAim = Extract<ActionDraft, { mode: "skirmish" | "tech_attack" | "ram" | "grapple" | "improvised_attack" }>;

/** The `request_attack` `fireAt` sends for a {@link DirectAttackAim} at `targetId`. */
export function directAttackIntent(state: EncounterState, aim: DirectAttackAim, targetId: string): RequestAttack {
  const target = { kind: "unit" as const, id: targetId };
  switch (aim.mode) {
    case "skirmish":
      return buildAttackIntent(aim.attackerId, target, {
        weapon: aim.weaponId || undefined,
        profile: aim.profileId || undefined,
        accuracy: aim.accuracy,
        // GM-declared Overwatch: the NPC inspector's Overwatch row arms this same skirmish draft with
        // `reaction: true`, so the identical weapon/board-click flow fires as a reaction (spends the
        // reaction slot and the "overwatch" 1/round cap). Absent/false on an ordinary Skirmish arm.
        reaction: aim.reaction,
        // Ammo preselects pass through (absent = "ask me", and `buildAttackIntent` drops an empty
        // `choices`, so the live mid-resolve `choose` prompt fires as before). Opt-in effect riders
        // resolve here to explicit wire values, because absence means "use the authored default" for
        // them, not "ask me"; stale `optIn:` keys from a previously armed weapon are stripped. See
        // `combatInspector.ts`'s `ActionDraft.choices` doc.
        choices: resolveSkirmishFireChoices(state, aim.attackerId, aim),
        mode: weaponModeThisTurn({ state, attackerId: aim.attackerId, weaponKey: aim.weaponId }),
      });
    case "tech_attack":
      // A system-granted tech attack: `request_attack` with `tech: true` and the composite
      // `<itemId>:<actionIndex>` id as `weapon`, mirroring `quickTechIntent`'s own `tech_attack` route.
      // Never `request_activate`: that intent has no `tech`/roll concept.
      return buildAttackIntent(aim.attackerId, target, { weapon: aim.actionId, accuracy: aim.accuracy, tech: true });
    case "ram":
      // The knock-back push is pre-declared on the aim; the synthetic `act_ram` weapon resolves it.
      return buildAttackIntent(aim.attackerId, target, { weapon: "act_ram", choices: { ram_push: aim.push ? "push" : "no_push" } });
    case "grapple":
      return buildAttackIntent(aim.attackerId, target, { weapon: "act_grapple" });
    case "improvised_attack":
      return buildAttackIntent(aim.attackerId, target, { weapon: "act_improvised_attack" });
  }
}

type TechAim = { attackerId: string; optionId: string; accuracy: number };

/**
 * The intent an armed Quick Tech option sends at `targetId`. An "Invade ›" pick (`techMenuPick`)
 * is an Invade with its option pre-answered, so the post-hit menu never asks.
 */
export function quickTechPickIntent(aim: TechAim, targetId: string): RequestActivate | RequestAttack {
  const pick = techMenuPick(aim.optionId);
  return quickTechIntent(pick.option, { actor: aim.attackerId, target: targetId, accuracy: aim.accuracy, ...(pick.choices ? { choices: pick.choices } : {}) });
}

/** The one `request_full_tech` pick an armed Full Tech option sends at `targetId`, decoded as {@link quickTechPickIntent} decodes. */
export function fullTechPickIntent(aim: TechAim, targetId: string): RequestFullTech {
  const pick = techMenuPick(aim.optionId);
  return buildFullTechIntent(aim.attackerId, targetId, { option: pick.option, accuracy: aim.accuracy, ...(pick.choices ? { choices: pick.choices } : {}) });
}

/** The rolling `request_attack` a Quick or Full Tech option routes to, or `null` (Scan, Lock On, Bolster, unpicked). */
function techOptionAttack(aim: TechAim, targetId: string): RequestAttack | null {
  if (!aim.optionId || quickTechOption(techMenuPick(aim.optionId).option) === undefined) return null;
  const intent = quickTechPickIntent(aim, targetId);
  return intent.type === "request_attack" ? intent : null;
}

/**
 * The `request_attack` the armed `aim` would resolve as against `targetId`, or `null` when it does
 * not roll one (Activate, Scan, Lock On, Bolster, an unarmed Barrage card, a lapsed hold).
 *
 * Beyond {@link directAttackIntent}'s modes: Quick and Full Tech map through `quickTechIntent`, the
 * function `fireAt` sends through (a Full Tech pick is one Quick Tech option); a Barrage shot maps
 * to the plain attack with its weapon, profile and accuracy (`request_barrage` resolves the same
 * single-target attack); a release maps through `buildReleaseIntent`, as `fireAt` sends it.
 */
export function attackIntentForAim(state: EncounterState, aim: ActionDraft, targetId: string): RequestAttack | null {
  switch (aim.mode) {
    case "skirmish":
    case "tech_attack":
    case "ram":
    case "grapple":
    case "improvised_attack":
      return directAttackIntent(state, aim, targetId);
    case "quick_tech":
    case "full_tech":
      return techOptionAttack(aim, targetId);
    case "barrage":
      if (!aim.weaponId) return null;
      return buildAttackIntent(aim.attackerId, { kind: "unit", id: targetId }, { weapon: aim.weaponId, profile: aim.profileId || undefined, accuracy: aim.accuracy });
    case "release": {
      const held = heldAction(state.activation, aim.attackerId);
      return held ? buildReleaseIntent(aim.attackerId, { kind: "unit", id: targetId }, held) : null;
    }
    default:
      return null;
  }
}
